import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectUserDetails } from "store/user/account/selectors";

const pick = require("lodash/pick");
import { ADDRESS_TYPES, BANK_ACCOUNT_TYPES, PHONE_TYPES, USER_KYC_DETAILS_INPUT } from "constants/index";
import SideModal from "components/SideModal";
import PersonalData from "./PersonalData";
import IdentificationData from "./IdentificationData";
import KycProvider from "./KycProvider";
import { useFormattedMessage } from "hooks";
import { fetchKYCStart, setKYCFailure, setKYCStart, updateKYCFormData } from "store/user/kyc/actions";
import {
    selectCurrenKYCStatus,
    selectHasUserInitiated,
    selectKYCError,
    selectKYCSubmitting,
    selectKycFormData,
    selectKycSubmittingSuccess,
} from "store/user/kyc/selectors";
import { KYCTileAPI } from "api";

const cookiesPkg = require("js-cookie");
const FINX_COOKIE = require("common/constants");

const messages = {
    kycMainHeading: "your_identification",
};

export const RenderKycStep = ({
    step,
    userAdditionalData,
    setStep,
    setUserDetails,
    onCloseButtonClick,
    isWithSecondaryMarket,
    onPersonalBack,
    updateKycForm,
    open,
    isKYCTileAPILoading,
    isKycSubmitSuccess,
    kycError,
    isInheritUserEnabled,
}) => {
    const [backSlideEffect, setBackSlideEffect] = useState("");

    // If KYC submitted successfully
    useEffect(() => {
        if (isKycSubmitSuccess) {
            setStep("kycProvider");
            setBackSlideEffect("step-slide-left");
        }
    }, [isKycSubmitSuccess]);

    function renderStep(step) {
        const userPersonalData = (userAdditionalData && userAdditionalData.personal) || {};

        if (
            !(
                userPersonalData.TypeGender === "female" ||
                userPersonalData.TypeGender === "male" ||
                userPersonalData.TypeGender === "diverse"
            )
        ) {
            userPersonalData.TypeGender = "";
        }
        const identificationData =
            (userAdditionalData &&
                userAdditionalData.identification &&
                Object.keys(userAdditionalData.identification).length &&
                userAdditionalData.identification) ||
            {};

        switch (step) {
            case "identificationData":
                return (
                    <IdentificationData
                        isSubmitButtonLoading={isKYCTileAPILoading}
                        open={open}
                        onCloseButtonClick={onCloseButtonClick}
                        backSlideEffect={backSlideEffect}
                        onBack={() => {
                            if (setStep) {
                                setStep("personalData");
                                setBackSlideEffect("step-slide-right");
                            }
                        }}
                        data={identificationData}
                        onSubmit={(values) => {
                            if (!isInheritUserEnabled) {
                                updateKycForm?.({
                                    ...identificationData,
                                    identification: values,
                                });
                                const userDetails = { ...userAdditionalData, identification: values };
                                setUserDetails(userDetails);
                            }
                        }}
                        kycError={kycError}
                        isInheritUserEnabled={isInheritUserEnabled}
                    />
                );
            case "kycProvider":
                return (
                    <KycProvider
                        open={open}
                        isRequiredBackButton
                        backSlideEffect={backSlideEffect}
                        onBack={() => {
                            if (setStep) {
                                setStep("identificationData");
                                setBackSlideEffect("step-slide-right");
                            }
                        }}
                        isInheritUserEnabled={isInheritUserEnabled}
                    />
                );
            case "personalData":
            default:
                return (
                    <PersonalData
                        open={open}
                        isWithSecondaryMarket={isWithSecondaryMarket}
                        isRequiredBackButton={isWithSecondaryMarket}
                        backSlideEffect={backSlideEffect}
                        onBack={() => {
                            if (isWithSecondaryMarket) {
                                onPersonalBack();
                                setBackSlideEffect("step-slide-right");
                            }
                        }}
                        data={userPersonalData}
                        onSubmit={(values) => {
                            setStep && setStep("identificationData");
                            !isInheritUserEnabled &&
                                updateKycForm?.({
                                    ...userPersonalData,
                                    personal: values,
                                });
                            setBackSlideEffect("step-slide-left");
                        }}
                    />
                );
        }
    }
    return renderStep(step);
};

const KycModal = ({
    open,
    onCloseButtonClick,
    onBackButtonClick,
    isCloseArrowOnRight,
    userAdditionalData,
    setKYCStartAPI,
    fetchKYCStart,
    updateKycForm,
    currentKYCStatus,
    isKYCSubmitting,
    isKycSubmitSuccess,
    hasUserInitiatedKYC,
    kycError,
    setKYCFailure,
}) => {
    const inheritUserToken = cookiesPkg.get(FINX_COOKIE.INHERIT_USER_TOKEN);
    const inheritUserEmail = cookiesPkg.get(FINX_COOKIE.INHERIT_USER_EMAIL);
    const isInheritUserEnabled = !!(inheritUserToken && inheritUserEmail);

    const [step, setStep] = useState("personalData");
    const [isKYCTileAPILoading, setKYCTileAPILoading] = useState(false);

    const { formatMessage } = useFormattedMessage();

    const setUserDetailsAPI = (kycData) => {
        setKYCTileAPILoading(true);

        let kycDetails = { ...kycData.personal, ...kycData.identification };
        kycDetails = pick(kycDetails, USER_KYC_DETAILS_INPUT);

        const {
            TypeTitle,
            Country,
            BirthCountry,
            Nationality,
            MaritalStatus,
            IsEffectaCustomerInformationConfirmed,
            ...restWithoutTypeTitle
        } = kycDetails;

        const KYCTileAPIPayload = {
            ...restWithoutTypeTitle,
            TypeTitle: TypeTitle?.value,
            TypePhone: PHONE_TYPES.TypePhoneMobile,
            TypeBankAccount: BANK_ACCOUNT_TYPES.TypeBankAccountUserPrimary,
            TypeAddress: ADDRESS_TYPES.TypeAddressPrimary,
            Country: Country?.value,
            BirthCountry: BirthCountry?.value,
            Nationality: Nationality?.value,
            MaritalStatus: MaritalStatus?.value,
            IsEffectaCustomerInformationConfirmed: IsEffectaCustomerInformationConfirmed === true ? "true" : "false",
        };

        KYCTileAPI(KYCTileAPIPayload)
            .then((res) => {
                setKYCTileAPILoading(false);

                if (res?.data?.data?.kycTile?.user?.Email) {
                    setKYCStartAPI({
                        user: kycDetails,
                        currentKYCStatus,
                    });
                }
            })
            .catch(() => {
                setKYCTileAPILoading(false);
            });
    };

    useEffect(() => {
        open && setStep("personalData");
    }, [open]);

    useEffect(() => {
        open && !isInheritUserEnabled && fetchKYCStart();
    }, [open]);

    useEffect(() => {
        !isInheritUserEnabled && setKYCFailure("");
    }, [step]);

    const onClose = () => {
        onCloseButtonClick();
        setStep("personalData");
    };

    const memoizedRenderKycStep = useMemo(() => {
        return (
            <RenderKycStep
                open={open}
                setStep={(step) => (step === null ? onClose() : setStep(step))}
                step={step}
                updateKycForm={updateKycForm}
                userAdditionalData={userAdditionalData}
                setUserDetails={setUserDetailsAPI}
                onCloseButtonClick={onClose}
                isKYCTileAPILoading={isKYCTileAPILoading}
                isKycSubmitSuccess={isKycSubmitSuccess}
                kycError={kycError}
                isInheritUserEnabled={isInheritUserEnabled}
            />
        );
    }, [step, userAdditionalData, isKYCSubmitting, isKycSubmitSuccess, hasUserInitiatedKYC, isKYCTileAPILoading, open]);

    return (
        <SideModal
            className="kyc-modal"
            heading={formatMessage(messages.kycMainHeading)}
            open={open}
            isBackArrowOnTop={step === "personalData"}
            isCloseArrowOnRight={isCloseArrowOnRight}
            onBackButtonClick={onBackButtonClick}
            onCloseButtonClick={onClose}
        >
            {memoizedRenderKycStep}
        </SideModal>
    );
};

const mapStateToProps = createStructuredSelector({
    userAdditionalData: selectKycFormData,
    userData: selectUserDetails,
    currentKYCStatus: selectCurrenKYCStatus,
    isKYCSubmitting: selectKYCSubmitting,
    isKycSubmitSuccess: selectKycSubmittingSuccess,
    hasUserInitiatedKYC: selectHasUserInitiated,
    kycError: selectKYCError,
});

const mapDispatchToProps = (dispatch) => ({
    setKYCStartAPI: (data) => dispatch(setKYCStart(data)),
    fetchKYCStart: () => dispatch(fetchKYCStart()),
    updateKycForm: (data) => dispatch(updateKYCFormData(data)),
    setKYCFailure: (data) => dispatch(setKYCFailure(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(KycModal);
